import { useCallback } from 'react';
import { Navigate, useOutletContext } from 'react-router';
import { Empty, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { FolderOpenIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { projectFoldersQuery } from '@/entities/project-folder';
import { ROUTES } from '@/shared/config/routes';
import { EmptyStateTitle } from '@/shared/ui';
import { RAIL_PAGE_SIZE } from './components/ApprovalFolderRail';
import { ApprovalLayout } from './components/ApprovalLayout';
import { RailToggleButton } from './components/RailToggleButton';
import type { ProjectDetailContext } from './folders.types';
import { useTranslation } from 'react-i18next';

/**
 * The approval section before any folder is open.
 *
 * The rail lists the folders and is the one source of truth for which one is
 * selected, so this route only ever renders transiently — while the first
 * folder is still loading or the fetch failed — or, when the project truly
 * has no folders yet, until one is created. The moment a folder is there, it
 * hands off to it rather than asking anyone to pick.
 */
export function FoldersListPage() {
  const { t } = useTranslation('projects');

  const { project, openCreateFolderModal } = useOutletContext<ProjectDetailContext>();
  // The same page the rail lists, so the two agree on whether the project has
  // any folders at all and the query is served from one cache entry.
  const { data: folders } = useQuery(
    projectFoldersQuery(project.platformProjectId, { page: 1, pageSize: RAIL_PAGE_SIZE }),
  );
  const firstFolder = folders?.items[0];

  const handleCreateFolder = useCallback(() => {
    openCreateFolderModal();
  }, [openCreateFolderModal]);

  if (firstFolder) {
    return (
      <Navigate
        to={ROUTES.PROJECT_FOLDER_DETAIL(project.platformProjectId, firstFolder.id)}
        replace
      />
    );
  }

  return (
    <ApprovalLayout projectId={project.platformProjectId} onCreateFolder={handleCreateFolder}>
      <div className="flex min-h-0 flex-1 flex-col gap-section rounded-lg bg-card p-section">
        {/* The same header row the open folder draws, so the toggle sits at one
            height across both. */}
        <header className="flex h-10 items-center">
          <RailToggleButton />
        </header>

        <Empty role="status" data-testid="approval-no-selection" className="flex-1">
          <EmptyHeader>
            <FolderOpenIcon className="size-6 text-muted-foreground" aria-hidden />
            <EmptyStateTitle>{t('projectDetail.foldersListPage.noFoldersYet')}</EmptyStateTitle>
            <EmptyDescription>
              {t('projectDetail.foldersListPage.noFoldersYetDescription')}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    </ApprovalLayout>
  );
}
